import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, X, UserCheck } from 'lucide-react';

// Editable "confirm customer" step shown in the AI chat before a proposal is
// actually created — stops wrong-name quotes from slipping through.
export const ConfirmProposalCard = ({ pending, sessionId, onCreated }) => {
  const [name, setName] = useState(pending.customer_name || '');
  const [email, setEmail] = useState(pending.customer_email || '');
  const [phone, setPhone] = useState(pending.customer_phone || '');
  const [status, setStatus] = useState('pending'); // pending | creating | created | cancelled

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13,
    border: '1px solid #cbd5e1', borderRadius: 6, outline: 'none', background: '#fff',
  };
  const labelStyle = { fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 3, marginTop: 10 };

  const confirm = async () => {
    if (!name.trim()) { toast.error('Customer name is required'); return; }
    setStatus('creating');
    try {
      const r = await axios.post('/ai/confirm-proposal', {
        session_id: sessionId,
        request: {
          ...pending.request,
          customer_name: name.trim(),
          customer_email: email.trim() || null,
          customer_phone: phone.trim() || null,
        },
        generate_mockups: pending.generate_mockups || null,
      });
      if (r.data?.success) {
        setStatus('created');
        onCreated(r.data);
      } else {
        setStatus('pending');
        toast.error(r.data?.error || 'Failed to create proposal');
      }
    } catch (e) {
      setStatus('pending');
      toast.error(e.response?.data?.detail || 'Failed to create proposal');
    }
  };

  if (status === 'created') {
    return (
      <div style={{ marginTop: 10, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 13, color: '#15803d', fontWeight: 600 }} data-testid="confirm-card-done">
        ✓ Confirmed — proposal created for {name}
      </div>
    );
  }
  if (status === 'cancelled') {
    return (
      <div style={{ marginTop: 10, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#94a3b8' }} data-testid="confirm-card-cancelled">
        Cancelled — tell me what to change and I'll re-quote it.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: 14, maxWidth: 420 }} data-testid="confirm-proposal-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 800, color: '#92400e' }}>
        <UserCheck size={16} /> Confirm customer before creating
      </div>
      {(pending.items_summary || []).length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: '#78716c' }}>
          {pending.items_summary.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
      <label style={labelStyle}>Customer Name *</label>
      <input value={name} onChange={(e) => setName(e.target.value)} style={fieldStyle} data-testid="confirm-customer-name" placeholder="Full name / company" />
      <label style={labelStyle}>Email</label>
      <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" style={fieldStyle} data-testid="confirm-customer-email" placeholder="customer@email.com" />
      <label style={labelStyle}>Phone</label>
      <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" style={fieldStyle} data-testid="confirm-customer-phone" placeholder="305-555-0100" />
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          onClick={confirm}
          disabled={status === 'creating'}
          data-testid="confirm-create-proposal-btn"
          style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#10b981', color: '#fff', border: 'none', padding: '10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}
        >
          {status === 'creating' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Create Proposal
        </button>
        <button
          onClick={() => setStatus('cancelled')}
          disabled={status === 'creating'}
          data-testid="confirm-cancel-btn"
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', padding: '10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          <X size={13} /> Cancel
        </button>
      </div>
    </div>
  );
};

export default ConfirmProposalCard;
